import { useState, useEffect } from 'react';

interface RedisKey {
  key: string;
  type: 'string' | 'hash' | 'list' | 'set' | 'zset';
  ttl: number; // -1 = no expiry, -2 = key doesn't exist
  size: number;
  value?: unknown;
}

interface RedisStats {
  connectedClients: number;
  usedMemory: string;
  usedMemoryPeak: string;
  totalKeys: number;
  hitRate: number;
  opsPerSec: number;
  uptime: number;
}

// Redis data - requires backend API connection
// TODO: Connect to /api/v1/debug/redis endpoint when available

export const RedisInspector = () => {
  const [keys, setKeys] = useState<RedisKey[]>([]);
  const [stats, setStats] = useState<RedisStats | null>(null);
  const [selectedKey, setSelectedKey] = useState<RedisKey | null>(null);
  const [searchPattern, setSearchPattern] = useState('*');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);

  // Load Redis data from backend API
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // TODO: Fetch from /api/v1/debug/redis when available
      // For now, show not connected - no fake data
      setKeys([]);
      setStats(null);
      setIsConnected(false);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Filter keys by pattern
  const filteredKeys = keys.filter(k => {
    if (searchPattern === '*') return true;
    const regex = new RegExp(searchPattern.replace(/\*/g, '.*'));
    return regex.test(k.key);
  });

  const formatTTL = (ttl: number): string => {
    if (ttl === -1) return '∞ (no expiry)';
    if (ttl === -2) return 'N/A';
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
    if (ttl < 86400) return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
    return `${Math.floor(ttl / 86400)}d ${Math.floor((ttl % 86400) / 3600)}h`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'string': return 'text-green-400';
      case 'hash': return 'text-blue-400';
      case 'list': return 'text-yellow-400';
      case 'set': return 'text-purple-400';
      case 'zset': return 'text-pink-400';
      default: return 'text-gray-400';
    }
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 rounded-lg p-3 border border-red-500/30">
        <p className="text-sm">
          <span className="text-red-400 font-medium">Redis Status:</span>{' '}
          <span className="text-gray-200">
            {isConnected === null ? 'Connecting...' : isConnected ? (
              <>
                <span className="text-green-400">Connected</span> • 
                {stats && <> {stats.totalKeys} keys, {stats.usedMemory} used, {Math.round(stats.hitRate * 100)}% hit rate</>}
              </>
            ) : (
              <span className="text-red-400">Connection failed</span>
            )}
          </span>
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-red-400 mb-2">📊 Server Stats</h4>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-900 rounded p-2 text-center">
              <div className="text-xs text-gray-500">Memory</div>
              <div className="text-lg font-bold text-red-400">{stats.usedMemory}</div>
              <div className="text-[10px] text-gray-600">peak: {stats.usedMemoryPeak}</div>
            </div>
            <div className="bg-gray-900 rounded p-2 text-center">
              <div className="text-xs text-gray-500">Hit Rate</div>
              <div className="text-lg font-bold text-green-400">{Math.round(stats.hitRate * 100)}%</div>
              <div className="text-[10px] text-gray-600">{stats.opsPerSec} ops/s</div>
            </div>
            <div className="bg-gray-900 rounded p-2 text-center">
              <div className="text-xs text-gray-500">Uptime</div>
              <div className="text-lg font-bold text-blue-400">{formatUptime(stats.uptime)}</div>
              <div className="text-[10px] text-gray-600">{stats.connectedClients} clients</div>
            </div>
          </div>
        </div>
      )}

      {/* Key Browser */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-red-400 mb-2">🔑 Key Browser</h4>
        
        {/* Search */}
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="Search pattern (e.g., cache:*)"
            value={searchPattern}
            onChange={(e) => setSearchPattern(e.target.value)}
            className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs font-mono"
          />
          <button
            onClick={() => setSearchPattern('*')}
            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
          >
            Clear
          </button>
        </div>

        {/* Keys List */}
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-4 text-gray-500 text-xs">Loading keys...</div>
          ) : filteredKeys.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-xs">No keys found</div>
          ) : (
            filteredKeys.map((k) => (
              <div
                key={k.key}
                onClick={() => setSelectedKey(selectedKey?.key === k.key ? null : k)}
                className={`flex items-center p-2 rounded cursor-pointer text-xs ${
                  selectedKey?.key === k.key
                    ? 'bg-red-900/50 border border-red-500'
                    : 'bg-gray-900 hover:bg-gray-850'
                }`}
              >
                <span className={`w-12 font-mono ${getTypeColor(k.type)}`}>{k.type}</span>
                <span className="flex-1 text-gray-300 font-mono truncate">{k.key}</span>
                <span className="w-16 text-right text-gray-500">{formatTTL(k.ttl)}</span>
                <span className="w-16 text-right text-gray-600">{formatBytes(k.size)}</span>
              </div>
            ))
          )}
        </div>

        {/* Key count */}
        <div className="text-[10px] text-gray-600 mt-2">
          Showing {filteredKeys.length} of {keys.length} keys
        </div>
      </div>

      {/* Selected Key Details */}
      {selectedKey && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-red-400">🔍 Key Details</h4>
            <button
              onClick={() => setSelectedKey(null)}
              className="text-gray-500 hover:text-white text-xs"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs mb-2">
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Key:</span>
              <span className="ml-2 text-gray-300 font-mono">{selectedKey.key}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Type:</span>
              <span className={`ml-2 font-mono ${getTypeColor(selectedKey.type)}`}>{selectedKey.type}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">TTL:</span>
              <span className={`ml-2 ${selectedKey.ttl < 300 && selectedKey.ttl > 0 ? 'text-yellow-400' : 'text-gray-300'}`}>
                {formatTTL(selectedKey.ttl)}
              </span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Size:</span>
              <span className="ml-2 text-gray-300">{formatBytes(selectedKey.size)}</span>
            </div>
          </div>

          <div>
            <span className="text-gray-500 text-xs">Value:</span>
            <pre className="bg-gray-900 rounded p-2 mt-1 text-[10px] text-gray-400 overflow-auto max-h-24 font-mono">
              {typeof selectedKey.value === 'string' 
                ? selectedKey.value 
                : JSON.stringify(selectedKey.value, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2">
        <button className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded text-xs opacity-50 cursor-not-allowed" disabled>
          🗑️ Flush Pattern
        </button>
        <button className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded text-xs opacity-50 cursor-not-allowed" disabled>
          📊 Memory Analysis
        </button>
      </div>
      <p className="text-[10px] text-gray-600 text-center">Write operations disabled in debug panel (read-only)</p>
    </div>
  );
};

